import React, { useState, useEffect } from 'react';
import '../styles/TranscripcionesList.css';

// const APIURL = "http://127.0.0.1:8000/";
const APIURL = "https://transcripciones.cnccol.com/";

const TranscripcionesList = () => {
  const [transcripciones, setTranscripciones] = useState([]);
  const [filteredTranscripciones, setFilteredTranscripciones] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false); // Estado para manejar la carga
  const itemsPerPage = 10;

  // Función para obtener las transcripciones del backend
  const obtenerTranscripciones = async () => {
    setIsLoading(true); // Activar el spinner
    try {
      const response = await fetch(`${APIURL}api/obtener_transcripciones/`);
      const data = await response.json();
      setTranscripciones(data.transcripciones);
      setFilteredTranscripciones(data.transcripciones);
    } catch (error) {
      console.error("Error al obtener transcripciones:", error);
    } finally {
      setIsLoading(false); // Desactivar el spinner
    }
  };

  // Función para filtrar las transcripciones según el término de búsqueda
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = transcripciones.filter((transcripcion) =>
      transcripcion.carpeta.toLowerCase().includes(term)
    );
    setFilteredTranscripciones(filtered);
    setCurrentPage(1);
  };

  // Paginación
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

    // Función para avanzar a la página siguiente
    const handleNextPage = () => {
        if (currentPage < Math.ceil(filteredTranscripciones.length / itemsPerPage)) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Función para retroceder a la página anterior
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

  const paginatedTranscripciones = filteredTranscripciones.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Función para generar números de páginas visibles
  const getVisiblePages = () => {
    const totalPages = Math.ceil(filteredTranscripciones.length / itemsPerPage);
    const maxVisible = 5; // Máximo de páginas visibles
    
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    const pages = [];
    const halfVisible = Math.floor(maxVisible / 2);
    
    let start = Math.max(1, currentPage - halfVisible);
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    // Ajustar el inicio si estamos cerca del final
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    // Agregar primera página y puntos suspensivos si es necesario
    if (start > 1) {
      pages.push(1);
      if (start > 2) {
        pages.push('...');
      }
    }
    
    // Agregar páginas del rango visible
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    // Agregar puntos suspensivos y última página si es necesario
    if (end < totalPages) {
      if (end < totalPages - 1) {
        pages.push('...');
      }
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="flex flex-col w-full p-6 bg-white rounded-xl shadow-lg overflow-hidden z-[5] relative">
      <h3 className="text-xl font-montserrat font-bold text-primary mb-6">
        Transcripciones Realizadas
      </h3>

      <button
        onClick={obtenerTranscripciones}
        className="btn btn-primary w-full mb-6 flex items-center justify-center gap-2 font-montserrat"
        disabled={isLoading}
      >
        {isLoading && (
          <span className="loading loading-spinner loading-sm"></span>
        )}
        {isLoading ? "Cargando..." : "Ver Últimas 50 Transcripciones"}
      </button>

      <div className="form-control mb-6">
        <input
          type="text"
          placeholder="Buscar transcripción..."
          value={searchTerm}
          onChange={handleSearch}
          className="input input-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>

      <div className="overflow-y-auto max-h-[40vh] scrollbar-thin scrollbar-thumb-primary scrollbar-track-gray-200">
        {paginatedTranscripciones.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {paginatedTranscripciones.map((transcripcion, index) => (
              <div key={index} className="py-4 first:pt-0 last:pb-0">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-montserrat font-semibold text-primary">
                    {transcripcion.carpeta}
                  </h4>
                  <span className="text-sm text-gray-500">
                    {new Date(transcripcion.fecha_txt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex gap-4">
                  <a 
                    href={transcripcion.docx_url} 
                    download 
                    className="text-sm text-primary hover:text-primary-light transition-colors flex items-center gap-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Descargar .docx
                  </a>
                  <a 
                    href={transcripcion.txt_url} 
                    download 
                    className="text-sm text-primary hover:text-primary-light transition-colors flex items-center gap-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Descargar .txt
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No se encontraron transcripciones.
          </div>
        )}
      </div>

      {filteredTranscripciones.length > itemsPerPage && (
        <div className="flex flex-col items-center gap-3 mt-6 h-20 justify-center">
          <div className="text-xs text-gray-600">
            Página {currentPage} de {Math.ceil(filteredTranscripciones.length / itemsPerPage)} 
            ({filteredTranscripciones.length} transcripciones total)
          </div>
          
          <div className="flex justify-center items-center gap-2 w-full">
            <button 
              onClick={handlePreviousPage} 
              className="btn btn-xs btn-outline min-w-[60px]" 
              disabled={currentPage === 1}
            >
              ‹ Ant
            </button>
            
            <div className="join mx-2">
              {getVisiblePages().map((page, index) => (
                <button
                  key={index}
                  onClick={() => page !== '...' && handlePageChange(page)}
                  disabled={page === '...'}
                  className={`btn btn-xs join-item min-w-[32px] ${
                    currentPage === page ? "btn-primary" : 
                    page === '...' ? "btn-ghost cursor-default" : "btn-ghost"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button 
              onClick={handleNextPage} 
              className="btn btn-xs btn-outline min-w-[60px]"
              disabled={currentPage === Math.ceil(filteredTranscripciones.length / itemsPerPage)}
            >
              Sig ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscripcionesList;
